/* eslint-disable @khan/frontend/require-stories -- context provider that is covered by tests*/
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Clickable from "@khanacademy/wonder-blocks-clickable";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {focusStyles} from "@khanacademy/wonder-blocks-styles";
import {semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";

import {DEFAULT_SORT_ORDER} from "./constants.ts";
import type {Column, Sort} from "./types.ts";

import sortAscendingIcon from "@phosphor-icons/core/bold/sort-ascending-bold.svg";
import sortDescendingIcon from "@phosphor-icons/core/bold/sort-descending-bold.svg";

interface Props<T> {
    /**
     * The column to render.
     */
    column: Column<T>;
    /**
     * Callback when the column is sorted.
     */
    onSort: (nextSort: Sort) => void;
    /**
     * The current sort state.
     */
    sort: Sort;
}

/**
 * Renders a sortable cell in the table header.
 * Can't use a button because the column label isn't always a string.
 */
export function SortableHeader<T>({
    column,
    onSort,
    sort,
}: Props<T>): React.ReactElement {
    const isBeingSorted = sort.orderBy === column.dataKey;
    const sortOrder = isBeingSorted
        ? sort.order
        : (column.sortable ?? DEFAULT_SORT_ORDER);

    return (
        <Clickable
            onClick={() => {
                onSort({orderBy: column.dataKey ?? "", order: sortOrder});
            }}
            style={styles.sortable}
            hideDefaultFocusRing={true}
        >
            {() => (
                <>
                    {column.label}
                    <PhosphorIcon
                        color={
                            semanticColor.core.foreground.instructive.default
                        }
                        icon={
                            sortOrder === "ascending"
                                ? sortAscendingIcon
                                : sortDescendingIcon
                        }
                        testId={`sort-icon-${sortOrder}`}
                    />
                </>
            )}
        </Clickable>
    );
}

const styles = StyleSheet.create({
    sortable: {
        display: "inline-flex",
        gap: sizing.size_040,
        ":hover": {
            textDecoration: "underline",
        },
        // Only show the focus ring when the user is navigating with the
        // keyboard.
        ":focus-visible": {
            borderRadius: sizing.size_020,
            ...focusStyles.focus[":focus-visible"],
        },
        ":active": {
            textDecoration: "underline",
        },
    },
});
